import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { X, Search, Loader2, Users, ArrowRightLeft } from "lucide-react";
import { SearchRequestBaseModel } from "../../../models/SearchRequestModel";
import { merchantApiService } from "../../../services/api/merchants/MerchantApiService";
import { toastService } from "../../../services/toastService";
import { storageService } from "../../../services/StorageService";
import { utils } from "../../../utils/utils";
import { userApiService } from "../../../services/api/users/UsersApiService";
import { User, AdditionalDataEnum } from "../../../models/User";
import history from "../../../history";
import { TransactionReportDetails, TransactionRequestModel } from "../../../models/Transactions";
import { transactionsReportApiService } from "../../../services/api/operations/TransactionsReportApiService";
import { SelectItem } from "primereact/selectitem";
import {
    Account_Number_Length,
    AppConstants,
    EMAIL_REGEX,
    ONLY_ALPHA_NUMERIC_REGEX,
    Only_Digit_REGEX,
    Roles,
    TRX_ID_MAX_Length,
    TRX_ID_MIN_Length,
} from "../../../models/AppConstants";

interface SearchModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface SearchFilter {
    label: string;
    value: string;
}

const searchFilters: SearchFilter[] = [
    { label: "Customer First Name", value: "FirstName" },
    { label: "Customer Last Name", value: "LastName" },
    { label: "Personal Identification Number", value: "PersonalIdentificationNumber" },
    { label: "Customer Id", value: "CbsId" },
    { label: "IBAN", value: "Iban" },
    { label: "TRX Id", value: "TRX-ID" },
];

const searchFiltersType = {
    CustomerId: "CbsId",
    Email: "Email",
    IBAN: "Iban",
    TRXId: "TRX-ID",
    PID: "PersonalIdentificationNumber",
};

enum AdminFilterTypes {
    AccountNumber = "AccountNumber",
}

enum LeadsViewFilterTypes {
    Email = "Email",
    PhoneNumber = "PhoneNumber",
}

const SuperAdminFilters: SearchFilter[] = [...searchFilters, { label: "Account Number", value: AdminFilterTypes.AccountNumber }];
const LeadsViewFilters: SearchFilter[] = [
    { label: LeadsViewFilterTypes.Email, value: LeadsViewFilterTypes.Email },
    { label: "Phone Number", value: LeadsViewFilterTypes.PhoneNumber },
];

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterBy, setFilterBy] = useState("");
    const [selectedMerchant, setSelectedMerchant] = useState("");
    const [merchants, setMerchants] = useState<SelectItem[]>([]);
    const [filters, setFilters] = useState<SearchFilter[]>([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    // Load data on first open
    useEffect(() => {
        if (isOpen && !isInitialized) {
            loadData();
        }
    }, [isOpen, isInitialized]);

    // Handle ESC key
    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [isOpen, onClose]);

    const hasRole = (role: string) => {
        return storageService
            .getRoles()
            .map((a: string) => a.toLowerCase())
            .includes(role.toLowerCase());
    };

    const loadData = async () => {
        const roles = storageService.getRoles();
        if (!roles || roles.length === 0) return;

        let filteredFilters: SearchFilter[] = [];
        let loadedMerchants: SelectItem[] = [];
        const superAdmin = hasRole(Roles.SUPER_ADMIN);
        const isLeads = hasRole(Roles.MerchantLeads);
        const isMassPaymentExecutor = hasRole(Roles.MassPaymentExecutor);

        if (!isLeads || superAdmin) {
            if (!isMassPaymentExecutor || isLeads || superAdmin) {
                const merchantResult = await merchantApiService.getSelectItem();
                if (merchantResult.success) {
                    loadedMerchants = merchantResult.data;
                }
            }
        }

        if (superAdmin) {
            filteredFilters = [...SuperAdminFilters, ...LeadsViewFilters];
        } else if (isLeads) {
            filteredFilters = LeadsViewFilters;
        } else {
            filteredFilters = [...searchFilters, ...LeadsViewFilters];
        }

        setIsSuperAdmin(superAdmin);
        setMerchants(loadedMerchants);
        setSelectedMerchant(
            superAdmin
                ? ""
                : isLeads
                    ? storageService.getLoggedInUserMerchantId().toString()
                    : (loadedMerchants.find((i: SelectItem) => i.value == storageService.getLoggedInUserMerchantId().toString())?.value as string ?? "")
        );
        setFilters(filteredFilters);
        setIsInitialized(true);
    };

    const validateSearchTerm = (filter: string, term: string): boolean => {
        if (filter == searchFiltersType.Email && !EMAIL_REGEX.test(term)) {
            toastService.error("Email is not valid.");
            return false;
        } else if (
            filter == searchFiltersType.IBAN &&
            !(ONLY_ALPHA_NUMERIC_REGEX.test(term) && term.length >= AppConstants.IbanMinLength && term.length <= AppConstants.IbanMaxLength)
        ) {
            toastService.error("IBAN is not valid.");
            return false;
        } else if (
            filter == SuperAdminFilters.find((f) => f.value == AdminFilterTypes.AccountNumber)?.value &&
            !(Only_Digit_REGEX.test(term) && term.length == Account_Number_Length)
        ) {
            toastService.error("Account Number is not valid.");
            return false;
        } else if (
            filter == searchFiltersType.TRXId &&
            !(ONLY_ALPHA_NUMERIC_REGEX.test(term) && term.length >= TRX_ID_MIN_Length && term.length <= TRX_ID_MAX_Length)
        ) {
            toastService.error("Transaction Id is not valid.");
            return false;
        } else if (filter == searchFiltersType.PID && !(Only_Digit_REGEX.test(term) && term.length >= 4 && term.length <= 20)) {
            toastService.error("Personal Identification Number is not valid.");
            return false;
        }
        return true;
    };

    const searchTransaction = async () => {
        setIsLoading(true);
        const request: TransactionRequestModel = { transactionId: searchTerm };
        const transactionResult = await transactionsReportApiService.getTransactionById(request);

        if (!transactionResult.success) {
            setIsLoading(false);
            toastService.error(transactionResult.message);
            return;
        }
        if (!transactionResult.data) {
            setIsLoading(false);
            toastService.error("Transaction not found");
            return;
        }

        const extractTransaction: TransactionReportDetails[] = Array.isArray(transactionResult)
            ? transactionResult
            : [transactionResult.data];

        const queryParams = new URLSearchParams({
            searchTransaction: JSON.stringify(extractTransaction),
        }).toString();

        history.replace({ pathname: "/all-transactions", search: `?${queryParams}` });
        setIsLoading(false);
        onClose();
    };

    const searchIndividuals = async () => {
        setIsLoading(true);
        const searchRequest = new SearchRequestBaseModel();
        searchRequest.filters = [{ columnName: filterBy, operator: "Contains", value: searchTerm }];
        searchRequest.merchantId = selectedMerchant;
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        const usersResult = await userApiService.getAll(searchRequest);
        setIsLoading(false);

        if (!usersResult.success) {
            toastService.error(usersResult.message);
            return;
        }
        if (usersResult.totalCount == 0) {
            toastService.error("Records not found.");
            return;
        }

        const queryParams = new URLSearchParams({
            searchInput: JSON.stringify(searchRequest.filters),
            totalUsers: String(usersResult.totalCount),
            userData: JSON.stringify(usersResult.data),
            fromSearch: "true",
        }).toString();

        history.replace({ pathname: "/users", search: `?${queryParams}` });
        onClose();
    };

    const handleSearch = async () => {
        if (!filterBy) {
            toastService.error("Please select a search type.");
            return;
        }
        if (!searchTerm.trim()) {
            toastService.error("Please enter a search term.");
            return;
        }
        if (!validateSearchTerm(filterBy, searchTerm)) return;

        if (
            !selectedMerchant &&
            (filterBy == searchFiltersType.IBAN ||
                filterBy == SuperAdminFilters.find((f) => f.value == AdminFilterTypes.AccountNumber)?.value)
        ) {
            toastService.error("Merchant not Selected.");
            return;
        }

        if (filterBy === searchFiltersType.TRXId) {
            await searchTransaction();
        } else {
            await searchIndividuals();
        }
    };

    const showMerchantDropdown =
        isSuperAdmin &&
        (filterBy == searchFiltersType.IBAN ||
            filterBy == SuperAdminFilters.find((f) => f.value == AdminFilterTypes.AccountNumber)?.value);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex items-start justify-center min-h-screen pt-20 px-4">
                <div className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-2xl animate-scale-in">
                    {/* Header with search input */}
                    <div className="p-5 border-b border-gray-200 dark:border-neutral-700">
                        <div className="flex items-center gap-3">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-neutral-500" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    maxLength={AppConstants.IbanMaxLength}
                                    onChange={(e) => setSearchTerm(e.target.value.trim())}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSearch();
                                    }}
                                    placeholder="Search customers, transactions, accounts..."
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl text-gray-900 dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all text-sm"
                                />
                                {isLoading && (
                                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-600 dark:text-primary-400 animate-spin" />
                                )}
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Search Type Pills */}
                        <div className="mt-4">
                            <p className="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-2 uppercase tracking-wider">Search Type</p>
                            <div className="flex flex-wrap gap-2">
                                {filters.map((filter) => (
                                    <button
                                        key={filter.value}
                                        onClick={() => setFilterBy(filter.value)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                            filterBy === filter.value
                                                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700"
                                                : "text-gray-600 dark:text-neutral-400 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600"
                                        }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Merchant Dropdown (conditional) */}
                        {showMerchantDropdown && (
                            <div className="mt-3">
                                <p className="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-2 uppercase tracking-wider">Merchant</p>
                                <select
                                    value={selectedMerchant}
                                    onChange={(e) => setSelectedMerchant(e.target.value)}
                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg text-sm text-gray-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                                >
                                    <option value="">Select Merchant...</option>
                                    {merchants.map((m) => (
                                        <option key={String(m.value)} value={String(m.value)}>
                                            {m.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Content area */}
                    <div className="p-8">
                        {!filterBy ? (
                            <div className="text-center">
                                <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                                    <Search className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                                </div>
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    Select a search type to get started
                                </p>
                            </div>
                        ) : !searchTerm ? (
                            <div className="text-center">
                                <div className="w-12 h-12 bg-gray-100 dark:bg-neutral-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                                    {filterBy === searchFiltersType.TRXId ? (
                                        <ArrowRightLeft className="h-6 w-6 text-gray-400 dark:text-neutral-500" />
                                    ) : (
                                        <Users className="h-6 w-6 text-gray-400 dark:text-neutral-500" />
                                    )}
                                </div>
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    Enter a search term and press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-gray-100 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600 rounded">Enter</kbd> to search
                                </p>
                            </div>
                        ) : (
                            <div className="text-center">
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-gray-100 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600 rounded">Enter</kbd> or click Search to find results
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-3 bg-gray-50 dark:bg-neutral-900/50 border-t border-gray-200 dark:border-neutral-700 rounded-b-2xl flex items-center justify-between">
                        <p className="text-xs text-gray-400 dark:text-neutral-500">
                            Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-gray-100 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600 rounded">ESC</kbd> to close
                        </p>
                        <button
                            onClick={handleSearch}
                            disabled={isLoading || !filterBy || !searchTerm.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                        >
                            {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Search className="h-4 w-4" />
                            )}
                            Search
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default SearchModal;
